// Shared route-permission config for the React SPA.
// MUST stay in lock-step with src/api/Helper/permissions.py::PROTECTED_ROUTES.
// Keys match the `path` prop on the <Route> elements in AppRoutes.tsx.

export const ROLES = {
  SYSTEM_ADMIN: "systemAdmin",
  PAYROLL_MANAGER: "payrollManager",
  PAYROLL_ANALYST: "payrollAnalyst",
  USER: "user",
} as const;

export type RoleKey = (typeof ROLES)[keyof typeof ROLES];

// Ordered highest -> lowest privilege.  Used by hasPermission() for the
// hierarchical "higher roles inherit lower" check.
export const ROLE_HIERARCHY: RoleKey[] = [
  ROLES.SYSTEM_ADMIN,
  ROLES.PAYROLL_MANAGER,
  ROLES.PAYROLL_ANALYST,
  ROLES.USER,
];

export interface RouteConfig {
  permission: RoleKey | null; // null = any authenticated user
  description: string;
}

// Route keys here MUST match keys in the server's PROTECTED_ROUTES map.
export const ROUTE_CONFIG: Record<string, RouteConfig> = {
  dashboard: { permission: ROLES.USER, description: "Dashboard" },
  insights: { permission: ROLES.USER, description: "Validation insights" },
  rule: { permission: ROLES.PAYROLL_ANALYST, description: "Rules page" },
  rulebook: { permission: ROLES.PAYROLL_MANAGER, description: "Rulebook upload" },
  admin: { permission: ROLES.SYSTEM_ADMIN, description: "Admin console" },
};

export type PermissionsMap = Partial<Record<RoleKey, boolean>>;

// Hierarchical check.  Mirrors check_hierarchical_permission() on the server.
export function hasPermission(
  permissions: PermissionsMap | undefined,
  required: RoleKey | null | undefined,
): boolean {
  if (!permissions) return false;
  if (!required) return !!permissions[ROLES.USER];

  const requiredIndex = ROLE_HIERARCHY.indexOf(required);
  if (requiredIndex === -1) return false;

  for (let i = 0; i <= requiredIndex; i++) {
    if (permissions[ROLE_HIERARCHY[i]]) return true;
  }
  return false;
}
